import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories, dramas, regions, years } from '@/mocks/dramas';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function Category() {
  const { slug } = useParams();
  const [activeCategory, setActiveCategory] = useState(slug || 'guochan');
  const [activeRegion, setActiveRegion] = useState('all');
  const [activeYear, setActiveYear] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    setActiveRegion('all');
    setActiveYear('all');
  }, [activeCategory]);

  const filteredDramas = useMemo(() => {
    let result = dramas;
    if (activeCategory !== 'all') {
      result = result.filter((d) => d.categorySlug === activeCategory);
    }
    if (activeRegion !== 'all') {
      const regionMap: Record<string, string> = {
        mainland: '中国大陆',
        korea: '韩国',
        usa: '美国',
        japan: '日本',
        hongkong: '中国香港',
        taiwan: '中国台湾',
      };
      result = result.filter((d) => d.region === regionMap[activeRegion]);
    }
    if (activeYear !== 'all') {
      if (activeYear === '2026') result = result.filter((d) => d.year === 2026);
      else if (activeYear === '2025') result = result.filter((d) => d.year === 2025);
      else if (activeYear === '2024') result = result.filter((d) => d.year === 2024);
      else if (activeYear === '2023') result = result.filter((d) => d.year === 2023);
      else if (activeYear === '2020-2022') result = result.filter((d) => d.year >= 2020 && d.year <= 2022);
      else if (activeYear === '2010s') result = result.filter((d) => d.year >= 2010 && d.year <= 2019);
      else if (activeYear === 'older') result = result.filter((d) => d.year < 2010);
    }
    return result;
  }, [activeCategory, activeRegion, activeYear]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  useEffect(() => {
    document.title = `${currentCategory?.name || '剧集分类'} - 6080影视 | 电视剧全集免费高清在线观看`;
  }, [currentCategory]);

  const breadcrumbs = [
    { name: '首页', href: '/' },
    { name: currentCategory?.name || '分类', href: '' },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${currentCategory?.name || '全部剧集'} - 6080影视`,
    description: `6080影视${currentCategory?.name || ''}分类页面，免费高清在线观看。`,
    url: `https://www.6080ys.com/category/${activeCategory}`,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.href ? `https://www.6080ys.com${crumb.href}` : undefined,
    })),
  };

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: currentCategory?.name || '全部剧集',
    itemListElement: filteredDramas.map((drama, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'TVSeries',
        name: drama.title,
        url: `https://www.6080ys.com/article/${drama.id}`,
        genre: drama.category,
        numberOfEpisodes: drama.episodeCount,
        actor: drama.actors.map((actor) => ({ '@type': 'Person', name: actor })),
        director: { '@type': 'Person', name: drama.director },
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }} />
      <main className="min-h-screen pt-20">
        {/* Breadcrumb */}
        <nav aria-label="面包屑导航" className="max-w-[1400px] mx-auto px-4 md:px-6 mb-6">
          <ol className="flex items-center gap-2 text-sm">
            {breadcrumbs.map((crumb, index) => (
              <li key={crumb.name} className="flex items-center">
                {index > 0 && <span className="text-foreground-600 mr-2">&gt;</span>}
                {crumb.href ? (
                  <Link to={crumb.href} className="text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
                    {crumb.name}
                  </Link>
                ) : (
                  <span className="text-foreground-300">{crumb.name}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-2">
            {currentCategory?.name || '全部剧集'}
          </h1>
          <p className="text-sm text-foreground-500 mb-8">共 {filteredDramas.length} 部剧集</p>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-950'
                    : 'bg-background-200 text-foreground-400 hover:bg-background-300 hover:text-foreground-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Region filter */}
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="text-xs text-foreground-500 py-2 mr-1">地区:</span>
            {regions.map((r) => (
              <button
                key={r.slug}
                onClick={() => setActiveRegion(r.slug)}
                className={`px-3 py-1 rounded-full text-xs transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeRegion === r.slug
                    ? 'bg-secondary-500 text-foreground-50'
                    : 'bg-background-200 text-foreground-500 hover:bg-background-300'
                }`}
              >
                {r.name}
              </button>
            ))}
          </div>

          {/* Year filter */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="text-xs text-foreground-500 py-2 mr-1">年份:</span>
            {years.map((y) => (
              <button
                key={y.slug}
                onClick={() => setActiveYear(y.slug)}
                className={`px-3 py-1 rounded-full text-xs transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeYear === y.slug
                    ? 'bg-secondary-500 text-foreground-50'
                    : 'bg-background-200 text-foreground-500 hover:bg-background-300'
                }`}
              >
                {y.name}
              </button>
            ))}
          </div>

          {/* View toggle */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`w-8 h-8 rounded flex items-center justify-center text-sm cursor-pointer ${
                  viewMode === 'grid'
                    ? 'bg-background-300 text-foreground-200'
                    : 'bg-background-200 text-foreground-500 hover:bg-background-300'
                }`}
              >
                <i className="ri-grid-fill"></i>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`w-8 h-8 rounded flex items-center justify-center text-sm cursor-pointer ${
                  viewMode === 'list'
                    ? 'bg-background-300 text-foreground-200'
                    : 'bg-background-200 text-foreground-500 hover:bg-background-300'
                }`}
              >
                <i className="ri-list-check"></i>
              </button>
            </div>
            <p className="text-xs text-foreground-500">{filteredDramas.length} 部剧集</p>
          </div>

          {/* Drama Grid/List */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filteredDramas.map((drama) => (
                <Link
                  key={drama.id}
                  to={`/article/${drama.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative rounded-lg overflow-hidden mb-3" style={{ aspectRatio: '2/3' }}>
                    <img
                      src={drama.cover}
                      alt={drama.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="bg-primary-500 text-background-950 text-xs font-bold px-2 py-0.5 rounded-md">{drama.rating}</span>
                    </div>
                    <div className="absolute top-2 right-2">
                      <span className={`text-xs px-2 py-0.5 rounded-md ${
                        drama.status === '更新中'
                          ? 'bg-accent-500/90 text-foreground-50'
                          : 'bg-background-900/70 text-foreground-300'
                      }`}>{drama.status}</span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                      <span className="text-sm text-foreground-50 bg-primary-500/90 px-4 py-1.5 rounded-full">立即观看</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-500 transition-colors duration-200 truncate">{drama.title}</h3>
                  <p className="text-xs text-foreground-500 mt-1">{drama.actors.slice(0, 2).join(' / ')} · {drama.year}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredDramas.map((drama) => (
                <Link
                  key={drama.id}
                  to={`/article/${drama.id}`}
                  className="group flex gap-4 bg-background-50 rounded-xl p-4 border border-background-300/60 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative rounded-lg overflow-hidden flex-shrink-0" style={{ width: '80px', aspectRatio: '2/3' }}>
                    <img
                      src={drama.cover}
                      alt={drama.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-500 transition-colors duration-200">{drama.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2">{drama.description}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-primary-500 font-medium">{drama.rating}</span>
                      <span className="text-xs text-foreground-600">|</span>
                      <span className="text-xs text-foreground-500">{drama.category}</span>
                      <span className="text-xs text-foreground-600">|</span>
                      <span className="text-xs text-foreground-500">{drama.updateInfo}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {filteredDramas.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-600 mb-4"></i>
              <p className="text-foreground-500">暂无符合条件的剧集</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}